/**
 * Product Metrics Component
 * Displays usage metrics for an enabled product
 */

import React from 'react';
import { Row, Col, Statistic, Progress, Card } from 'antd';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';
import { ArrowUpOutlined, ArrowDownOutlined } from '@ant-design/icons';
import { ProductMetrics as ProductMetricsType } from '../../services/productFeaturesService';
import './ProductMetrics.css';

interface ProductMetricsProps {
  metrics: ProductMetricsType;
  productColor: string;
}

const ProductMetrics: React.FC<ProductMetricsProps> = ({ metrics, productColor }) => {
  const { totalUsage, monthlyUsage, dailyTrend, quota } = metrics;

  // Calculate usage percentage
  const usagePercentage = quota.total > 0 ? (quota.used / quota.total) * 100 : 0;

  // Prepare chart data
  const chartData = dailyTrend.map((value, index) => ({
    day: `${index + 1}`,
    usage: value,
  }));

  // Calculate trend (comparing last 7 days vs previous 7 days)
  const recentUsage = dailyTrend.slice(-7).reduce((sum, val) => sum + val, 0);
  const previousUsage = dailyTrend.slice(-14, -7).reduce((sum, val) => sum + val, 0);
  const trendPercentage = previousUsage > 0 
    ? ((recentUsage - previousUsage) / previousUsage) * 100 
    : 0;

  return (
    <div className="product-metrics">
      {/* Stats Cards */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col span={12}>
          <Card size="small" className="metric-card">
            <Statistic
              title="Toplam Kullanım"
              value={totalUsage}
              valueStyle={{ color: productColor, fontSize: 24 }}
            />
          </Card>
        </Col>
        <Col span={12}>
          <Card size="small" className="metric-card">
            <Statistic
              title="Bu Ay"
              value={monthlyUsage}
              valueStyle={{ fontSize: 24 }}
              prefix={
                trendPercentage > 0 ? (
                  <ArrowUpOutlined style={{ color: '#52c41a' }} />
                ) : trendPercentage < 0 ? (
                  <ArrowDownOutlined style={{ color: '#ff4d4f' }} />
                ) : null
              }
              suffix={
                trendPercentage !== 0 && (
                  <span style={{ fontSize: 14, color: '#8c8c8c' }}>
                    ({Math.abs(trendPercentage).toFixed(0)}%)
                  </span>
                )
              }
            />
          </Card>
        </Col>
      </Row>

      {/* Usage Chart */}
      {chartData.length > 0 && (
        <Card size="small" className="chart-card" style={{ marginBottom: 16 }}>
          <div style={{ marginBottom: 12 }}>
            <span style={{ fontSize: 14, fontWeight: 500, color: '#262626' }}>
              Son 30 Gün Kullanım Trendi
            </span>
          </div>
          <ResponsiveContainer width="100%" height={120}>
            <LineChart data={chartData}>
              <XAxis 
                dataKey="day" 
                tick={{ fontSize: 12 }}
                stroke="#d9d9d9"
                interval="preserveStartEnd"
              />
              <YAxis 
                tick={{ fontSize: 12 }}
                stroke="#d9d9d9"
                width={30}
              />
              <Tooltip 
                contentStyle={{ 
                  borderRadius: 8, 
                  border: '1px solid #f0f0f0',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
                }}
                labelFormatter={(value) => `Gün ${value}`}
                formatter={(value: number) => [value, 'Kullanım']}
              />
              <Line 
                type="monotone" 
                dataKey="usage" 
                stroke={productColor}
                strokeWidth={2}
                dot={false}
                activeDot={{ r: 4 }}
              />
            </LineChart>
          </ResponsiveContainer>
        </Card>
      )}

      {/* Quota Progress */}
      <Card size="small" className="quota-card">
        <div style={{ marginBottom: 12 }}>
          <Row justify="space-between" align="middle">
            <Col>
              <span style={{ fontSize: 14, fontWeight: 500, color: '#262626' }}>
                Kota Kullanımı
              </span>
            </Col>
            <Col>
              <span style={{ fontSize: 13, color: '#8c8c8c' }}>
                {quota.used.toLocaleString()} / {quota.total.toLocaleString()}
              </span>
            </Col>
          </Row>
        </div>
        <Progress 
          percent={Math.round(usagePercentage)} 
          strokeColor={
            usagePercentage >= 90 ? '#ff4d4f' : 
            usagePercentage >= 75 ? '#faad14' : 
            productColor
          }
          status={usagePercentage >= 100 ? 'exception' : 'active'}
          showInfo={false}
        />
        <div style={{ marginTop: 8, textAlign: 'center' }}>
          <span style={{ fontSize: 12, color: '#8c8c8c' }}>
            {quota.remaining.toLocaleString()} kullanım hakkı kaldı
          </span>
        </div>
      </Card>
    </div>
  );
};

export default ProductMetrics;
